import React, { useState, useEffect } from "react";
import ChatLayout from "../atoms/Chat/ChatLayout";
import ChatSidebar from "../atoms/Chat/ChatSidebar";
import ChatWindow from "../atoms/Chat/ChatWindow";
import ChatHeader from "../atoms/Chat/ChatHeader";
import ChatMessages from "../atoms/Chat/ChatMessages";
import ChatInput from "../atoms/Chat/ChatInput";
import { socketWex } from "../../utils/Socket";
import useWexClient from "../../hooks/Clients/wexClient,";

/**
 * PROPS:
 * chats: [{ id, name }]
 * conversations: [{ chatId, messages: [{ id, from, text, time, class, dataSend }] }]
 * activeChatId: string | null
 * onSelectChat: (chatId: string | null) => void
 * messageValue: string
 * onMessageChange: (val: string) => void
 * onSendMessage: () => void
 */
const ChatCabin = ({
  chats = [],
  conversations = {},
  activeChatId,
  onSelectChat,
  messageValue,
  onMessageChange,
  onSendMessage,
  onClose,
  serviceId = "",
  onUpdateMessageStatus = () => {},
  onAppendMessages = () => {},
}) => {
  const { requestWhatsAppContact } = useWexClient();
  const [previewImage, setPreviewImage] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isWhats, setIsWhats] = useState(false);
  const [isInternalChat, setIsInternalChat] = useState(false);
  const [whatsAppOpen, setWhatsAppOpen] = useState(true);

  const activeChat = activeChatId
    ? chats.find((c) => c.id === activeChatId)
    : null;

  const activeConversation = conversations[activeChatId];

/*   
  PARA DEBUGGEAR CHATS QUE LLEGUEN 
useEffect(() => {
    if (!chats) return;
   
  }, [chats]); */

  useEffect(() => {
    if (activeConversation) {
      if (typeof activeConversation.isWhatsAppOpen !== "undefined") {
        setWhatsAppOpen(activeConversation.isWhatsAppOpen);
      } else {
        setWhatsAppOpen(true);
      }
    }
  }, [activeConversation?.isWhatsAppOpen, activeChatId]);

  useEffect(() => {
    const handlerUpdateStatus = (data) => {
      console.log("Socket update-status-whatsapp-in-chat:", data);
      if (!data) return;

      const messageSid =
        data.messageSid ||
        data.MessageSid ||
        data.message_sid ||
        data.sid ||
        data._id ||
        data.id;
      const status = data.status || data.MessageStatus || data.SmsStatus;

      if (messageSid && status) {
        onUpdateMessageStatus(messageSid, status);
      }
    };

    socketWex.on("update-status-whatsapp-in-chat", handlerUpdateStatus);

    return () => {
      socketWex.off("update-status-whatsapp-in-chat", handlerUpdateStatus);
    };
  }, [activeChatId, onUpdateMessageStatus]);

  useEffect(() => {
    if (activeChat) {
      const isW = activeChat.type === "_WHATSAPP_ADJUSTER_";
      setIsWhats(isW);
      setIsInternalChat(!isW);
    } else {
      setIsWhats(false);
      setIsInternalChat(false);
    }
  }, [activeChat]);

  // Estado de modo oscuro con persistencia en localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      return localStorage.getItem("chat-theme-mode") === "dark";
    } catch (e) {
      return false;
    }
  });

  const handleToggleTheme = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("chat-theme-mode", next ? "dark" : "light");
      } catch (e) {}
      return next;
    });
  };

  // Comprobar si se puede enviar mensajes según la lógica original de ChatCabin
  const canSendMessage =
    !!activeChatId &&
    !!activeConversation &&
    (activeConversation.idHeader || activeConversation.idFolioBodie);

  const isInputDisabled = !canSendMessage || (isWhats && !whatsAppOpen);

  const normalizeWhatsAppPhone = (phone = "") => {
    const trimmed = String(phone || "").trim();

    if (!trimmed) return "";

    if (trimmed.startsWith("whatsapp:")) return trimmed;
    if (trimmed.startsWith("+")) return `whatsapp:${trimmed}`;

    return `whatsapp:+${trimmed}`;
  };

  const handleRequestContact = async () => {
    if (!activeChatId || !activeConversation || !isWhats) return;

    const folioId =
      activeConversation.idFolioBodie || activeConversation.idHeader;
    const recipientPhone = normalizeWhatsAppPhone(
      activeConversation.phone || activeChat?.phone || "",
    );

    if (!folioId || !recipientPhone) return;

    const body = {
      to: recipientPhone,
      idFolioBodie: folioId,
      idService: serviceId || "",
    };

    try {
      const response = await requestWhatsAppContact(body);
      try {
        onAppendMessages(activeChatId, response);
      } catch (err) {
        console.error("Error al anexar mensajes recibidos:", err);
      }
    } catch (error) {
      console.error("Error al solicitar contacto por WhatsApp:", error);
    }
  };

  const handleCloseChat = () => {
    onSelectChat(null);
    onMessageChange("");
  };

  return (
    <>
      <ChatLayout
        title="Centro de Mensajería"
        onClose={onClose}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        isDarkMode={isDarkMode}
      >
        {/* Contenedor del Sidebar con transición de colapso */}
        <div
          style={{
            width: isSidebarOpen ? "260px" : "0px",
            opacity: isSidebarOpen ? 1 : 0,
            overflow: "hidden",
            transition:
              "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Sidebar con el buscador y la lista de chats */}
          <ChatSidebar
            chats={chats}
            conversations={conversations}
            activeChatId={activeChatId}
            onSelectChat={onSelectChat}
            isDarkMode={isDarkMode}
            onToggleTheme={handleToggleTheme}
            isWhats={isWhats}
          />
        </div>

        {/* Panel del Chat Activo o Pantalla de Bienvenida */}
        <ChatWindow activeChat={activeChat} isDarkMode={isDarkMode}>
          {activeChat && (
            <>
              {/* Cabecera del chat seleccionado */}
              <ChatHeader
                title={
                  activeChat.profile?.code
                    ? activeChat.profile.code
                    : activeChat.name
                }
                subtitle={
                  canSendMessage
                    ? "Caso asignado"
                    : "Esperando datos del caso..."
                }
                avatarId={activeChat.id}
                showCloseButton={true}
                onClose={handleCloseChat}
                isWhats={isWhats}
              />

              {/* Contenedor del flujo de mensajes con scroll automático */}
              <ChatMessages
                messages={activeConversation?.messages || []}
                onImagePreview={setPreviewImage}
              />

              {/* Campo para redactar y enviar mensajes */}
              <ChatInput
                value={messageValue}
                onChange={onMessageChange}
                onSend={onSendMessage}
                onRequestContact={handleRequestContact}
                placeholder={
                  !canSendMessage
                    ? "Esperando asignación del folio..."
                    : isWhats && !whatsAppOpen
                      ? "Ventana de WhatsApp cerrada (más de 24 horas sin interacción)"
                      : "Escribe un mensaje..."
                }
                disabled={isInputDisabled}
                contactRequestDisabled={!isInputDisabled}
                isDarkMode={isDarkMode}
              />
            </>
          )}
        </ChatWindow>
      </ChatLayout>

      {/* Visor de imágenes flotante en overlay */}
      {previewImage && (
        <div style={styles.imageOverlay} onClick={() => setPreviewImage(null)}>
          <div
            style={styles.previewContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewImage}
              alt="Previsualización"
              style={styles.imagePreview}
            />
            <button
              style={styles.closeOverlayBtn}
              onClick={() => setPreviewImage(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  imageOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(11,20,26,.9)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000,
  },
  previewContainer: {
    position: "relative",
    maxWidth: "85%",
    maxHeight: "85%",
  },
  imagePreview: {
    maxWidth: "100%",
    maxHeight: "80vh",
    objectFit: "contain",
    borderRadius: "4px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
  },
  closeOverlayBtn: {
    position: "absolute",
    top: "-40px",
    right: "0",
    background: "transparent",
    color: "#ffffff",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.8,
    ":hover": {
      opacity: 1,
    },
  },
};

export default ChatCabin;
